import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) {
        continue;
      }

      this.#loadRecommendations();
      break;
    }
  });

  #cachedRecommendations = {};
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  async #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      this.#handleError(new Error('Product ID and a section ID are required'));
      return;
    }

    if (recommendationsPerformed === 'true') return;

    try {
      const result = await this.#fetchCachedRecommendations(productId, sectionId, intent);

      if (!result.success) {
        if (!Shopify.designMode) {
          this.#handleError(new Error('Server returned ' + result.status));
        }
        return;
      }

      if (!result.data?.trim().length) {
        throw new Error('No recommendations available');
      }

      this.dataset.recommendationsPerformed = 'true';

      try {
        await morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });

        if (this.querySelector('.product-recommendations__skeleton-item')) {
          throw new Error('Recommendation hydration left the loading placeholders in place');
        }
      } catch (error) {
        this.#replaceRecommendations(result.data);
      }
    } catch (error) {
      this.#handleError(error);
    }
  }

  #replaceRecommendations(html) {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const replacement = documentFragment.querySelector('product-recommendations');

    if (!replacement) {
      throw new Error('Recommendation section was not found in the response');
    }

    replacement.dataset.recommendationsPerformed = 'true';
    this.replaceWith(replacement);
  }

  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = this.dataset.url + '&product_id=' + productId + '&section_id=' + sectionId + '&intent=' + intent;
    const cachedResponse = this.#cachedRecommendations[url];

    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });

      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
